import React, { Children, Fragment, PropsWithChildren, ReactElement, useMemo } from 'react';
import styled from '@emotion/styled';
import { BsPlus } from '@react-icons/all-files/bs/BsPlus';
import {
  BREAK_POINT_NAMES,
  BreakPoint,
  useResponsive,
} from '@bucketplace/design-system';

import { EditorPlaceholder } from '../../components';
import { GridData, INITIAL_GRID_DATA, VerticalAlign } from './types';
import { group, pad } from './utils';

export type GridProps = {
  [key in BreakPoint]?: GridData
};

export function Grid({ ...props }: PropsWithChildren<GridProps>): ReactElement {
  const { breakpoint } = useResponsive();
  const { children, ...breakpointDatas } = props;

  const currentBreakpoint = useMemo(() => {
    const matchBreakpoint = props[breakpoint as BreakPoint];
    if (matchBreakpoint) {
      return matchBreakpoint;
    }

    const maxBreakpointIndex = BREAK_POINT_NAMES.findIndex((name) => name === breakpoint);
    let currentBreakpoint = INITIAL_GRID_DATA;
    Object.entries(breakpointDatas).slice(0, maxBreakpointIndex).forEach(([key, value]) => {
      if (BREAK_POINT_NAMES.some((name) => name === key) && value) {
        currentBreakpoint = value as GridData;
      }
    });
    return currentBreakpoint;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint, breakpointDatas]);

  const { column, columnRatio, gap, verticalAlign } = currentBreakpoint;

  const renderChildren = children || renderPlaceholders(column);
  const childrenRows = group(Children.toArray(renderChildren), column);

  return (
    <div>
      { childrenRows.map((row, rowIndex) => (
        <GridRow key={`${rowIndex + 1}`} gap={gap} verticalAlign={verticalAlign}>
          { pad(row, column, <Placeholder />).map((child, index) => (
            <Fragment key={`${index + 1}`}>
              <GridItem ratio={columnRatio[index] ?? 0}>{ child }</GridItem>
              { index !== column - 1 && <Gutter width={gap} /> }
            </Fragment>
          )) }
        </GridRow>
      )) }
    </div>
  );
}

function Placeholder(): ReactElement {
  return <EditorPlaceholder icon={<BsPlus />} />;
}

export function renderPlaceholders(column: number): ReactElement[] {
  return Array(column)
    .fill(undefined)
    .map(() => <Placeholder />);
}

const GridRow = styled.div<{ gap: number, verticalAlign: VerticalAlign }>`
  display: flex;
  flex-flow: row wrap;
  margin-top: ${({ gap }) => gap}px;
  align-items: ${({ verticalAlign }) => verticalAlign};
  &:first-of-type {
    margin-top: 0;
  }
`;

const GridItem = styled.div<{
  ratio: number,
}>`
  flex: ${({ ratio }) => ratio} 0 0px;
  > * {
    height: 100%;
  }
`;

const Gutter = styled.div<{ width: number }>`
  flex: 0 0 ${({ width }) => width}px;
`;
